import { AccountType, AccountTypeForVersionManagement, Organization } from "types/orgTypes";
import {
  Button,
  Card,
  Divider,
  Group,
  Modal,
  MultiSelect,
  Stack,
  Table,
  Text,
  TextInput,
  Textarea,
  ThemeIcon,
  Title,
  Tooltip,
} from "@mantine/core";
import {
  patchOrganization,
  toggleOrganizationActive,
  uploadOrganizationLogoNoAuth,
} from "@utils/api-communication/RestApiCli";
import { useEffect, useState } from "react";

import { IconInfoCircle } from "@tabler/icons-react";
import OrgLogoView from "@components/common/OrgLogoView";
import { Role } from "@model/role";
import { checkRole, getRole } from "@utils/CommonUtil";
import { accountTypeValues } from "@constants/orgConstants";
import { withDemoAndDistributorTypes } from "@constants/accountTypeOptions";
import { createStyles } from "@mantine/emotion";
import { openConfirmation } from "@utils/redux-utils/actions/actions";
import { useDisclosure } from "@mantine/hooks";
import { useDispatch } from "react-redux";
import { useTranslation } from "react-i18next";

const useStyles = createStyles((_theme) => ({
  table: {
    "& td:nth-child(2)": {
      background: "transparent",
    },
    cursor: "pointer",
  },
}));

export default function OrganizationSettingsForm(props) {
  const [organization, setOrganization] = useState(null);
  const [opened, { open, close }] = useDisclosure(false);
  const [editMode, setEditMode] = useState(false);
  const { t } = useTranslation();
  const { classes } = useStyles();
  const dispatch = useDispatch();
  const userRole = getRole();
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [deleteError, setDeleteError] = useState("");

  const handleUpdateButton = () => {
    props?.updateOrgInfo(organization, handleOrgSuccessfullOrgUpdate(organization), () => {
      props?.updateOrganization();
      setEditMode(false);
      close();
    });
  };

  const handleOrgSuccessfullOrgUpdate = (org: Organization) => {
    if (org.logoFile) {
      uploadOrganizationLogoNoAuth(
        org.orgId,
        org.logoFile,
        () => regularCallback(false),
        (error) => regularCallback(true, error),
      );
    } else if (org.logoUrl === null) {
      patchOrganization(
        org.orgId,
        { logoUrl: null },
        () => regularCallback(false),
        (error) => regularCallback(true, error),
      );
    } else {
      regularCallback(false);
    }
  };

  const regularCallback = (isError: boolean, error?: any) => {
    if (isError) {
      if (error) {
        console.warn("Error occurred during organization update:", error);
      } else {
        console.warn("Error occurred during organization update (no error details provided)");
      }
    }
    props?.updateOrganization();
    setEditMode(false);
    close();
  };

  useEffect(() => {
    setOrganization(props.organization);
  }, [props, props.organization]);

  const handleInputChange = (event) => {
    setOrganization({
      ...organization,
      [event.target.name]: event.target.value,
    });
  };

  const handleLogoChange = (file) => {
    setOrganization({
      ...organization,
      logoUrl: null, // Remove logo url since it's going to be replaced.
      logoFile: file,
    });
  };

  const handleDeleteOrganization = () => {
    setDeleteError("");
    setDeleteLoading(true);
    const orgId = props.organization.orgId;
    toggleOrganizationActive(orgId, false, onDeleteSuccess, onDeleteError);
  };

  function onDeleteSuccess(_response) {
    setDeleteLoading(false);
    // Optionally, trigger a refresh or redirect
  }

  function onDeleteError(error) {
    setDeleteError(error.response?.data?.message || "Failed to deactivate organization");
    setDeleteLoading(false);
  }

  const getAccountTypeOptions = () => {
    const ato: Array<AccountType | AccountTypeForVersionManagement> = withDemoAndDistributorTypes(
      accountTypeValues,
      checkRole([Role.Admin]),
    );
    return ato.map((type) => ({ value: type, label: type.toUpperCase() }));
  };

  return organization ? (
    <>
      <Modal
        size={"lg"}
        opened={opened}
        onClose={close}
        title={t("OrganizationSettings.confirmModalHeader")}
      >
        <Text>{t("OrganizationSettings.confirmModalMessage")}</Text>
        <Table mb={"md"}>
          <tbody>
            <Table.Tr>
              <Table.Td> {t("OrganizationSettings.nameField")}</Table.Td>
              <Table.Td>{organization.name}</Table.Td>
            </Table.Tr>
            <Table.Tr>
              <Table.Td> {t("OrganizationSettings.addressField")}</Table.Td>
              <Table.Td>{organization.address}</Table.Td>
            </Table.Tr>
            <Table.Tr>
              <Table.Td>{t("OrganizationSettings.email")}</Table.Td>
              <Table.Td>{organization.email}</Table.Td>
            </Table.Tr>
            <Table.Tr>
              <Table.Td>{t("OrganizationSettings.accountType")}</Table.Td>
              <Table.Td>
                {organization.accountType
                  ?.map((type) => type.charAt(0).toUpperCase() + type.slice(1))
                  .join(", ")}
              </Table.Td>
            </Table.Tr>
            {(userRole === Role.Admin || userRole === Role.Distributor) && (
              <Table.Tr>
                <Table.Td>{t("OrganizationSettings.accountInfo")}</Table.Td>
                <Table.Td>{organization.accountInfo}</Table.Td>
              </Table.Tr>
            )}
            <Table.Tr>
              <Table.Td>
                <Text>{t("OrganizationSettings.logo")}</Text>
              </Table.Td>
              <Table.Td>
                <OrgLogoView
                  organization={organization}
                  isEditMode={false}
                  isPreview={true}
                  imageSize={{ width: 100, height: 100 }}
                />
              </Table.Td>
            </Table.Tr>
          </tbody>
        </Table>
        <Button.Group>
          <Button onClick={() => handleUpdateButton()} color="green">
            {t("OrganizationSettings.submit")}
          </Button>
          <Button
            color="red"
            onClick={() => {
              setEditMode(false);
              close();
              setOrganization(props.organization);
            }}
          >
            {t("OrganizationSettings.cancel")}
          </Button>
        </Button.Group>
      </Modal>
      <Stack w={"100%"}>
        <Card maw={"100%"} withBorder shadow="sm">
          <Title order={3}>{t("OrganizationSettings.organizationSettings")}</Title>
          <Table withColumnBorders={false} className={classes.table} mb={"md"}>
            <tbody>
              <Table.Tr>
                <Table.Td>
                  <Text>{t("OrganizationSettings.nameField")}</Text>
                </Table.Td>
                <Table.Td>
                  <TextInput
                    name="name"
                    value={organization.name}
                    onChange={handleInputChange}
                    style={{ flex: 1 }}
                    readOnly={!editMode}
                  />
                </Table.Td>
              </Table.Tr>
              <Table.Tr>
                <Table.Td>
                  <Group wrap="nowrap">
                    <Text>{t("OrganizationSettings.addressField")}</Text>
                    <Tooltip label={t("OrganizationSettings.addressFieldTooltip")}>
                      <ThemeIcon variant="light" radius="lg" color="yellow">
                        <IconInfoCircle style={{ width: "70%", height: "70%" }} />
                      </ThemeIcon>
                    </Tooltip>
                  </Group>
                </Table.Td>
                <Table.Td>
                  <TextInput
                    name="address"
                    value={organization.address}
                    onChange={handleInputChange}
                    style={{ flex: 1 }}
                    readOnly={!editMode}
                  />
                </Table.Td>
              </Table.Tr>
              <Table.Tr>
                <Table.Td>
                  <Text>{t("OrganizationSettings.email")}</Text>
                </Table.Td>
                <Table.Td>
                  <TextInput
                    name="email"
                    value={organization.email}
                    onChange={handleInputChange}
                    style={{ flex: 1 }}
                    readOnly={!editMode}
                  />
                </Table.Td>
              </Table.Tr>
              <Table.Tr>
                <Table.Td>
                  <Text>{t("OrganizationSettings.accountType")}</Text>
                </Table.Td>
                <Table.Td>
                  <MultiSelect
                    data={getAccountTypeOptions()}
                    value={organization.accountType || []}
                    onChange={(value) =>
                      handleInputChange({ target: { name: "accountType", value: value } })
                    }
                    style={{ flex: 1 }}
                    readOnly={
                      !editMode || (userRole !== Role.Admin && userRole !== Role.Distributor)
                    }
                  />
                </Table.Td>
              </Table.Tr>
              {(userRole === Role.Admin || userRole === Role.Distributor) && (
                <Table.Tr>
                  <Table.Td>
                    <Text>{t("OrganizationSettings.accountInfo")}</Text>
                  </Table.Td>
                  <Table.Td>
                    <Textarea
                      name="accountInfo"
                      value={organization.accountInfo}
                      onChange={handleInputChange}
                      style={{ flex: 1 }}
                      readOnly={!editMode}
                    />
                  </Table.Td>
                </Table.Tr>
              )}
              <Table.Tr>
                <Table.Td>
                  <Text>{t("OrganizationSettings.logo")}</Text>
                </Table.Td>
                <Table.Td>
                  <OrgLogoView
                    organization={organization}
                    isEditMode={editMode}
                    onLogoSelected={handleLogoChange}
                    imageSize={{ width: 100, height: 100 }}
                  />
                </Table.Td>
              </Table.Tr>
            </tbody>
          </Table>

          {editMode ? (
            <Button.Group>
              <Button onClick={open} color="green">
                {t("OrganizationSettings.submit")}
              </Button>
              <Button
                color="red"
                onClick={() => {
                  setEditMode(false);
                  close();
                  setOrganization(props.organization);
                }}
              >
                {t("OrganizationSettings.cancel")}
              </Button>
            </Button.Group>
          ) : (
            <Button.Group>
              <Button onClick={() => setEditMode(true)} color="blue" disabled={props.readOnly}>
                {t("OrganizationSettings.edit")}
              </Button>
              {(userRole === Role.Distributor || userRole === Role.Admin) && (
                <Button
                  color="red"
                  loading={deleteLoading}
                  onClick={() =>
                    dispatch(
                      openConfirmation(
                        t("OrganizationSettings.deleteOrgTitle"),
                        t("OrganizationSettings.deleteOrgConfirm"),
                        handleDeleteOrganization,
                        true,
                        t("cancelButton"),
                        t("OrganizationSettings.deleteOrgButton"),
                      ),
                    )
                  }
                >
                  {t("OrganizationSettings.deleteOrgButton")}
                </Button>
              )}
              {deleteError && (
                <Text c="red" mt="sm">
                  {deleteError}
                </Text>
              )}
            </Button.Group>
          )}

          {props.additionalContent && (
            <>
              <Divider my="md" />
              {props.additionalContent}
            </>
          )}
        </Card>
      </Stack>
    </>
  ) : null;
}
